import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { freight } from "../lib/freightApi.js";
import { DEFAULT_CONFIG, BRANCHES, EQUIPMENT, PERMS, NOTIFY, TMS_MODES, commitConfig, saveDraft, getDraft, canApprove, getConfig } from "../shared/brokerConfig.js";

const T = { ink: "#000000", ink2: "#5A5A5A", ink3: "#8C8C8C", ink4: "#BFBFBF", hair: "#DCDCDC", hairSoft: "#EDEDED", wash: "#F5F5F5" };
const sans = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif`;
const mono = `ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace`;

const TMS_OPTIONS = [
  { id: "powerbroker", name: "McLeod PowerBroker", note: "Loads, ratecons, carrier file, customer accounts, agent structure", hint: "pb_live_…", where: "Utilities → API Access. Needs read on orders, carriers, and customers." },
  { id: "aljex", name: "Aljex (Descartes)", note: "Loads, ratecons, carriers, customers", hint: "aljex_…", where: "Ask your Descartes rep for a credential." },
  { id: "tai", name: "Tai TMS", note: "Loads, ratecons, carriers, customers", hint: "tai_…", where: "Settings → API." },
  { id: "turvo", name: "Turvo", note: "Shipments, partners, documents", hint: "turvo_…", where: "Admin → Developer → API keys." },
];
// ⛔ NOT A COUNT OF ANYTHING. These were rendered as "Carriers on file — 1204" and
// "Do-not-use list — 37 carriers, imported" on a screen a broker finishes and then opens the
// Carriers tab, which is now LIVE and says "No carriers in your book yet". Before the wiring
// both sides were fiction and agreed; now one is real and they contradicted each other — and
// the direction that matters on a fraud screen is the do-not-use list: 37 imported, 0 blocked.
// Nothing imports a carrier book yet, so the screen must not say a number.

const CUSTOMER_LIST = ["Sunbelt Distribution", "Beaumont Polymers", "Midwest Grocers", "Delta Fabrication", "Metro Print Supply"];

const FIELD_MAP = [
  ["Carrier assignment", "orders.carrier_id", "write"], ["Truck and driver", "orders.tractor / driver_name", "write"],
  ["Appointment times", "stops.sched_arrive_early / late", "write"], ["Gate-in and gate-out", "stops.actual_arrival / departure", "write"],
  ["BOL, seal, weight", "orders.bol_num / seal_num / weight", "write"], ["POD signer and time", "stops.signed_by / delivered_at", "write"],
  ["Call notes", "orders.comments, prefixed ALFRED:", "write"], ["Customer rate", "orders.rate", "read, never spoken"],
  ["Carrier pay", "orders.carrier_pay", "read, never spoken"], ["Customer and carrier master", "customers.* / carriers.*", "read only"],
];

const WATCH_SAMPLE = [
  { ref: "GB-88104", lane: "Houston → Laredo", would: "Called 4 bidders back. Two cleared the rules, one failed the FMCSA callback, one was on your do-not-use list.", stop: "Would have handed the rate to Marisol — top bid was $180 over your posted number." },
  { ref: "GB-88117", lane: "Baytown → Memphis", would: "Verified Cypress Haul, took truck and driver, set both appointments.", stop: "Nothing. This one would have run end to end." },
  { ref: "GB-88129", lane: "Pharr → Kansas City", would: "Caught that the reefer carrier had no breakdown endorsement on file.", stop: "Would have stopped before dispatch and put it in compliance's queue." },
];

const FMCSA_CASES = {
  active: { ok: true, legalName: "Gulf Bridge Logistics LLC", dba: "Gulf Bridge Freight", usdot: "3902114", mc: "MC-1094882", entity: "Broker", status: "Active", granted: "Authority granted March 2019", bond: "BMC-84 surety, $75,000", bondSurety: "Great American Insurance", bondExpiry: "Renews Apr 2027", registeredEmail: "c•••••@gulfbridgelogistics.com", registeredPhone: "(713) •••-0288" },
  notfound: { ok: false, title: "No authority found", body: "FMCSA has no record for that number. Check the digits, or try your USDOT instead — a lot of newer authorities are DOT-only." },
  inactive: { ok: false, title: "Authority is not active", body: "FMCSA shows this authority as revoked or pending. Alfred can't place calls on behalf of an inactive authority, and carriers will check. Sort it out with FMCSA and come back." },
  bond: { ok: false, title: "Bond is not on file", body: "No active BMC-84 on record. Brokering without a bond isn't something we'll automate calls for. Your surety needs to file before we can provision a number." },
};
const NUMBER_CASES = {
  a: { attestation: "A", label: "Attestation A — full", note: "Your authority is verified and the number is registered to it." },
  b: { attestation: "B", label: "Attestation B — partial", note: "The carrier can vouch for the customer but not the number's origin. It will still connect, but some networks will label it. We'd rather fix this than ship it." },
  inuse: { attestation: null, label: "Number already in use", note: "That number is provisioned to another account. Pick a different one." },
};

/* ------------------------------- primitives -------------------------------- */
const Micro = ({ children, style }) => <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: T.ink3, ...style }}>{children}</div>;
const Tag = ({ children, solid }) => <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.ink}`, background: solid ? T.ink : "transparent", color: solid ? "#fff" : T.ink, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
const Soft = ({ children }) => <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.hair}`, color: T.ink2, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
function Button({ children, onClick, kind = "primary", disabled, size = "md" }) {
  const k = { primary: { background: T.ink, color: "#fff", border: `1px solid ${T.ink}` }, quiet: { background: "#fff", color: T.ink, border: `1px solid ${T.hair}` } }[kind];
  return <button onClick={onClick} disabled={disabled} className="alf-btn" style={{ ...k, font: "inherit", fontSize: size === "sm" ? 12.5 : 13.5, fontWeight: 500, letterSpacing: -0.15, padding: size === "sm" ? "6px 11px" : "9px 16px", borderRadius: 7, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1 }}>{children}</button>;
}
const inputBase = { width: "100%", font: "inherit", fontSize: 14, color: T.ink, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 7, padding: "9px 11px", outline: "none", boxSizing: "border-box" };
const Field = ({ label, value, onChange, placeholder, isMono, hint, half, children }) => (
  <div style={{ flex: half ? "1 1 46%" : "1 1 100%", minWidth: 0 }}>
    <Micro style={{ marginBottom: 5 }}>{label}</Micro>
    {children || <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={{ ...inputBase, fontFamily: isMono ? mono : sans }} />}
    {hint && <div style={{ fontSize: 12, color: T.ink3, marginTop: 5, lineHeight: 1.45 }}>{hint}</div>}
  </div>
);
const Select = ({ value, onChange, options }) => <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputBase, appearance: "none", cursor: "pointer" }}>{options.map((o) => <option key={o}>{o}</option>)}</select>;
const Row = ({ label, value, isMono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0" }}>
    <span style={{ fontSize: 13, color: T.ink3 }}>{label}</span>
    <span style={{ fontSize: 13, textAlign: "right", fontFamily: isMono ? mono : sans }}>{value}</span>
  </div>
);
const Pill = ({ on, onClick, children }) => (
  <button onClick={onClick} style={{ font: "inherit", fontSize: 12.5, padding: "6px 12px", borderRadius: 7, cursor: "pointer", border: `1px solid ${on ? T.ink : T.hair}`, background: on ? T.ink : "#fff", color: on ? "#fff" : T.ink2 }}>{children}</button>
);
function Check({ on, onChange, label, note }) {
  return (
    <button onClick={() => onChange(!on)} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", font: "inherit", width: "100%" }}>
      <span style={{ width: 17, height: 17, borderRadius: 4, flexShrink: 0, marginTop: 1, boxSizing: "border-box", border: `1px solid ${on ? T.ink : T.hair}`, background: on ? T.ink : "#fff", color: "#fff", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>{on ? "✓" : ""}</span>
      <span><span style={{ display: "block", fontSize: 13.5, lineHeight: 1.45 }}>{label}</span>{note && <span style={{ display: "block", fontSize: 12, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{note}</span>}</span>
    </button>
  );
}
const Toggle = ({ on, onChange }) => (
  <button onClick={() => onChange(!on)} aria-pressed={on} style={{ width: 42, height: 24, borderRadius: 999, padding: 2, cursor: "pointer", flexShrink: 0, background: on ? T.ink : "#fff", border: `1px solid ${on ? T.ink : T.hair}`, display: "flex", justifyContent: on ? "flex-end" : "flex-start" }}>
    <span style={{ width: 18, height: 18, borderRadius: 999, background: on ? "#fff" : T.ink4 }} />
  </button>
);
const Waveform = ({ active }) => (
  <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2.5, height: 14 }}>
    {[0, 1, 2, 3, 4].map((i) => <span key={i} className={active ? "alf-bar" : ""} style={{ width: 2, borderRadius: 1, background: T.ink, height: active ? 14 : 4, animationDelay: `${i * 0.11}s`, opacity: active ? 1 : 0.3 }} />)}
  </span>
);
const Head = ({ title, note }) => <div><div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.6 }}>{title}</div>{note && <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 5, lineHeight: 1.55, maxWidth: 560 }}>{note}</div>}</div>;
const Progress = ({ lines, at }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "4px 0" }}>
    {lines.map((s, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, opacity: i <= at ? 1 : 0.25 }}>
        {i < at ? <span style={{ width: 16, fontSize: 13 }}>✓</span> : i === at ? <span style={{ width: 16 }}><Waveform active /></span> : <span style={{ width: 16, textAlign: "center", color: T.ink4 }}>·</span>}
        <span style={{ fontSize: 13.5 }}>{s}</span>
      </div>
    ))}
  </div>
);
const Problem = ({ title, body, children }) => (
  <div className="alf-fade" style={{ border: `1px solid ${T.ink}`, borderRadius: 10, padding: "14px 16px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}><Tag solid>stopped</Tag><span style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>{title}</span></div>
    <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.55 }}>{body}</div>
    {children && <div style={{ marginTop: 12 }}>{children}</div>}
  </div>
);
function Steps({ list, current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {list.map((l, i) => (
        <React.Fragment key={l}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 17, height: 17, borderRadius: 999, fontSize: 9.5, fontFamily: mono, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", background: i < current ? T.ink : "#fff", color: i < current ? "#fff" : i === current ? T.ink : T.ink4, border: `${i === current ? 2 : 1}px solid ${i <= current ? T.ink : T.hair}` }}>{i < current ? "✓" : i + 1}</span>
            <span style={{ fontSize: 12.5, fontWeight: i === current ? 600 : 400, color: i <= current ? T.ink : T.ink3 }}>{l}</span>
          </span>
          {i < list.length - 1 && <span style={{ width: 14, height: 1, background: T.hair }} />}
        </React.Fragment>
      ))}
    </div>
  );
}
/** demo-only control for exercising the unhappy paths */
const Sim = ({ value, onChange, options }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 12px", border: `1px dashed ${T.hair}`, borderRadius: 8 }}>
    <Micro>Simulate</Micro>
    {options.map(([v, l]) => <Pill key={v} on={value === v} onClick={() => onChange(v)}>{l}</Pill>)}
  </div>
);

/* ------------------------------ step 1: TMS -------------------------------- */

function TmsStep({ cfg, set, onNext }) {
  const [phase, setPhase] = useState(cfg.tms.connected ? "done" : "pick");
  const [line, setLine] = useState(0);
  const [key, setKey] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [sim, setSim] = useState("ok");
  const [err, setErr] = useState(null);
  const [files, setFiles] = useState(0);
  const LINES = ["Authenticating", "Reading orders", "Indexing carrier file", "Mapping customer accounts", "Reading branch and agent structure"];
  const picked = TMS_OPTIONS.find((t) => t.id === cfg.tms.id) || TMS_OPTIONS[0];

  useEffect(() => {
    if (phase !== "testing") return;
    const fail = { badkey: 1, scope: 3, env: 0 }[sim];
    const stopAt = sim === "ok" ? LINES.length : fail;
    if (line < stopAt) { const t = setTimeout(() => setLine((n) => n + 1), 420); return () => clearTimeout(t); }
    const t = setTimeout(() => {
      if (sim === "ok") { set({ tms: { ...cfg.tms, connected: true, keyMask: `••••${key.slice(-4) || "1a7d"}`, lastError: null } }); setPhase("done"); }
      else {
        const messages = {
          badkey: ["That key was rejected", "PowerBroker returned 401. The key may be revoked, or copied with a trailing space. Generate a fresh one under Utilities → API Access."],
          scope: ["The key works, but it can't see carriers", "Authenticated fine and read orders, then got 403 on the carrier file. The key needs read scope on carriers and customers, not just orders."],
          env: ["That's a sandbox key against production", "The credential is a sandbox token but the environment is set to Production. Alfred would read an empty board and place no calls."],
        }[sim];
        setErr({ title: messages[0], body: messages[1] });
        set({ tms: { ...cfg.tms, lastError: messages[0] } });
        setPhase("failed");
      }
    }, 380);
    return () => clearTimeout(t);
  }, [phase, line]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Start with your TMS" note="Your TMS is the system of record. Connect it and the rest of setup fills itself in — branches, carriers, customers, and your do-not-use list." />

      {phase === "pick" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {TMS_OPTIONS.map((t) => (
            <button key={t.id} onClick={() => { set({ tms: { ...cfg.tms, id: t.id, name: t.name } }); setPhase("mode"); }} className="alf-row"
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, textAlign: "left", font: "inherit", padding: "15px 18px", background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 9, cursor: "pointer" }}>
              <span style={{ flex: 1 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 15, fontWeight: 500, letterSpacing: -0.25 }}>{t.name}</span>
                  {t.id === "powerbroker" && <Tag solid>supported first</Tag>}
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>{t.note}</span>
              </span>
              <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
            </button>
          ))}
        </div>
      )}

      {phase === "mode" && (
        <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>How do you want to start?</div>
          {Object.entries(TMS_MODES).map(([v, m]) => (
            <button key={v} onClick={() => set({ tms: { ...cfg.tms, mode: v } })} style={{ display: "flex", gap: 12, textAlign: "left", font: "inherit", cursor: "pointer", padding: "14px 16px", borderRadius: 9, background: "#fff", border: `1px solid ${cfg.tms.mode === v ? T.ink : T.hair}` }}>
              <span style={{ width: 15, height: 15, borderRadius: 999, marginTop: 2, flexShrink: 0, boxSizing: "border-box", border: `${cfg.tms.mode === v ? 5 : 1}px solid ${cfg.tms.mode === v ? T.ink : T.hair}`, background: "#fff" }} />
              <span>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 14.5, fontWeight: 500 }}>{m.label}</span>{v === "watch" && <Soft>no key needed</Soft>}</span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink2, marginTop: 3, lineHeight: 1.5 }}>{m.note}</span>
              </span>
            </button>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Button onClick={() => setPhase(cfg.tms.mode === "watch" ? "watch" : "key")}>Continue</Button>
            <Button kind="quiet" onClick={() => setPhase("pick")}>Back</Button>
          </div>
        </div>
      )}

      {phase === "watch" && (
        <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Upload ten recent rate confirmations</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5 }}>
              Alfred reads them and shows what it would have done on each — who it would have called, what it would have flagged, where it would have stopped. Nothing is dialed and nothing is written.
            </div>
            <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Button size="sm" onClick={() => setFiles(Math.min(files + 3, 10))}>Add ratecons</Button>
              <span style={{ fontSize: 12.5, color: T.ink3 }}>{files} of 10 uploaded</span>
            </div>
          </div>
          {files > 0 && (
            <div className="alf-fade" style={{ border: `1px solid ${T.ink}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "13px 18px", borderBottom: `1px solid ${T.hairSoft}`, fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>What Alfred would have done</div>
              {WATCH_SAMPLE.slice(0, Math.ceil(files / 3)).map((w) => (
                <div key={w.ref} style={{ padding: "13px 18px", borderTop: `1px solid ${T.hairSoft}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{w.ref}</span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{w.lane}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>{w.would}</div>
                  <div style={{ fontSize: 12.5, color: T.ink, marginTop: 5, lineHeight: 1.5 }}><Soft>where it stops</Soft> <span style={{ marginLeft: 6 }}>{w.stop}</span></div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={files === 0} onClick={() => { set({ tms: { ...cfg.tms, connected: true, keyMask: "watch mode" } }); setPhase("done"); }}>Continue in watch mode</Button>
            <Button kind="quiet" onClick={() => setPhase("mode")}>Back</Button>
          </div>
        </div>
      )}

      {(phase === "key" || phase === "testing" || phase === "failed") && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 580 }}>
          <Sim value={sim} onChange={setSim} options={[["ok", "Works"], ["badkey", "Key rejected"], ["scope", "Missing scope"], ["env", "Sandbox key"]]} />
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>{picked.name}</span>
              <Soft>{TMS_MODES[cfg.tms.mode].label}</Soft>
              <button onClick={() => setPhase("mode")} style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, color: T.ink2 }}>change</button>
            </div>
            {phase === "testing" ? <Progress lines={LINES} at={line} /> : (
              <>
                <Field label="API key" value={key} onChange={setKey} placeholder={picked.hint} isMono hint={picked.where} />
                <Field half label="Environment"><Select value={cfg.tms.env} onChange={(v) => set({ tms: { ...cfg.tms, env: v } })} options={["Production", "Sandbox"]} /></Field>
                {cfg.tms.mode === "full" && (
                  <div style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: 14 }}>
                    <button onClick={() => setShowMap((s) => !s)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", font: "inherit", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                      <span style={{ fontSize: 14, fontWeight: 500, flex: 1 }}>Exactly which fields Alfred reads and writes</span>
                      <span style={{ color: T.ink3, fontSize: 13, transform: showMap ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
                    </button>
                    {showMap && (
                      <div className="alf-fade" style={{ marginTop: 12 }}>
                        {FIELD_MAP.map(([label, field, mode]) => (
                          <div key={label} style={{ display: "flex", gap: 12, padding: "7px 0", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: 13, minWidth: 165 }}>{label}</span>
                            <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink2, flex: 1, minWidth: 175 }}>{field}</span>
                            {mode === "write" ? <Soft>write</Soft> : mode.startsWith("read,") ? <Tag>read only</Tag> : <Soft>read</Soft>}
                          </div>
                        ))}
                        <div style={{ fontSize: 12, color: T.ink2, marginTop: 10, lineHeight: 1.5 }}>
                          Alfred reads both rate fields — it needs them to know when a bid is over the ceiling — and never says either one out loud, to either side. It never writes them.
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {err && <Problem title={err.title} body={err.body} />}
                <div><Button disabled={!key.trim()} onClick={() => { setErr(null); setLine(0); setPhase("testing"); }}>{err ? "Try again" : "Test and connect"}</Button></div>
              </>
            )}
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ border: `1px solid ${T.ink}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.hairSoft}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.35 }}>{picked.name}</span>
              <Tag solid>{TMS_MODES[cfg.tms.mode].label}</Tag>
              <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, color: T.ink3 }}>{cfg.tms.env} · {cfg.tms.keyMask}</span>
            </div>
            <div style={{ padding: "12px 18px 16px" }}>
              {/* ⛔ No import exists, so there is no count. An em dash is the honest figure. */}
              <Row label="Open loads" value={"\u2014"} isMono />
              <Row label="Carriers on file" value={"\u2014"} isMono />
              <Row label="Customer accounts" value={"\u2014"} isMono />
              <Row label="Branches" value={BRANCHES.join(", ")} />
              <Row label="Do-not-use list" value="Nothing imported yet — add carriers on the Carriers tab" />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onNext}>Next: your authority</Button>
            <Button kind="quiet" onClick={() => { setPhase("mode"); set({ tms: { ...cfg.tms, connected: false } }); }}>Change mode</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------- step 2: the brokerage --------------------------- */

function BrokerageStep({ cfg, set, onBack, onNext }) {
  const [mode, setMode] = useState("mc");
  const [num, setNum] = useState("");
  const [sim, setSim] = useState("active");
  const [numSim, setNumSim] = useState("a");
  const [phase, setPhase] = useState(cfg.broker.verified ? "number" : "idle");
  const [line, setLine] = useState(0);
  const [code, setCode] = useState("");
  const [tries, setTries] = useState(0);
  const [codeErr, setCodeErr] = useState(null);
  const [found, setFound] = useState(cfg.broker.verified ? FMCSA_CASES.active : null);
  const LOOKUP = ["Querying FMCSA", "Checking broker authority", "Reading the BMC-84 bond"];

  useEffect(() => {
    if (phase !== "looking") return;
    if (line < LOOKUP.length) { const t = setTimeout(() => setLine((n) => n + 1), 420); return () => clearTimeout(t); }
    const t = setTimeout(() => { const c = FMCSA_CASES[sim]; setFound(c); setPhase(c.ok ? "found" : "problem"); }, 360);
    return () => clearTimeout(t);
  }, [phase, line]);

  function submitCode() {
    if (tries >= 3) { setCodeErr("Too many attempts. Wait fifteen minutes, or ask us to send a new code."); return; }
    if (code !== "111111" && code.length === 6) {
      const next = tries + 1;
      setTries(next);
      setCodeErr(next >= 3 ? "Too many attempts. Locked for fifteen minutes." : `That code doesn't match. ${3 - next} attempt${3 - next === 1 ? "" : "s"} left. Codes expire after ten minutes.`);
      return;
    }
    set({ broker: { ...cfg.broker, ...found, verified: true } });
    setPhase("number");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Your brokerage" note="MC or USDOT — we pull the authority and the bond from FMCSA." />

      {(phase === "idle" || phase === "looking" || phase === "problem") && (
        <>
          <Sim value={sim} onChange={setSim} options={[["active", "Active"], ["notfound", "Not found"], ["inactive", "Inactive"], ["bond", "No bond"]]} />
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 420 }}>
            <div style={{ display: "flex", border: `1px solid ${T.hair}`, borderRadius: 7, overflow: "hidden", width: "fit-content" }}>
              {[["mc", "MC number"], ["dot", "USDOT number"]].map(([v, l]) => <Pill key={v} on={mode === v} onClick={() => setMode(v)}>{l}</Pill>)}
            </div>
            <Field label={mode === "mc" ? "MC number" : "USDOT number"} value={num} onChange={setNum} placeholder={mode === "mc" ? "MC-1094882" : "3902114"} isMono />
            {phase === "looking" ? <Progress lines={LOOKUP} at={line} /> : <div><Button disabled={!num.trim()} onClick={() => { setLine(0); setPhase("looking"); }}>Look it up</Button></div>}
          </div>
          {phase === "problem" && found && <Problem title={found.title} body={found.body}><Button size="sm" kind="quiet" onClick={() => setPhase("idle")}>Try another number</Button></Problem>}
        </>
      )}

      {(phase === "found" || phase === "verify") && found && found.ok && (
        <div className="alf-fade" style={{ border: `1px solid ${T.ink}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.hairSoft}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.35 }}>{found.legalName}</span>
            <Tag solid>{found.entity}</Tag>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, color: T.ink3 }}>{found.mc} · USDOT {found.usdot}</span>
          </div>
          <div style={{ padding: "12px 18px 16px" }}>
            <Row label="Doing business as" value={found.dba} />
            <Row label="Authority" value={`${found.status} · ${found.granted}`} />
            <Row label="Surety bond" value={`${found.bond} · ${found.bondSurety}`} />
            <Row label="Bond renewal" value={found.bondExpiry} />
          </div>
        </div>
      )}

      {phase === "found" && (
        <div className="alf-fade" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button onClick={() => setPhase("verify")}>That's us</Button>
          <Button kind="quiet" onClick={() => { setPhase("idle"); setFound(null); }}>Not us</Button>
        </div>
      )}

      {phase === "verify" && (
        <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 14, maxWidth: 500 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Prove the authority is yours</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5 }}>A code to the contact FMCSA has on file — {found.registeredEmail} — not one typed in here. You're about to ask Alfred to verify carriers this way, so it starts with you.</div>
          </div>
          <Field label="Six-digit code" value={code} onChange={(v) => { setCode(v.replace(/\D/g, "").slice(0, 6)); setCodeErr(null); }} placeholder="••••••" isMono hint="In this prototype the code is 111111 — any other six digits exercises the failure path." />
          {codeErr && <div style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.5, border: `1px solid ${T.ink}`, borderRadius: 8, padding: "10px 12px" }}>{codeErr}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={code.length < 6 || tries >= 3} onClick={submitCode}>Verify</Button>
            <Button kind="quiet" onClick={() => { setTries(0); setCodeErr(null); }}>Send a new code</Button>
          </div>
        </div>
      )}

      {phase === "number" && (
        <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Sim value={numSim} onChange={setNumSim} options={[["a", "Attestation A"], ["b", "Attestation B"], ["inuse", "Number in use"]]} />
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>The number Alfred calls from</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 540 }}>
              Provisioned to your verified authority and registered for STIR/SHAKEN. Carriers screen hard — an unregistered line reads as "Spam Likely" and your callbacks go unanswered.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              {[{ n: "(713) 999-0221", area: "Houston, TX — local to your office" }, { n: "(346) 999-0148", area: "Houston metro" }, { n: "(888) 999-0310", area: "Toll free" }].map((n) => {
                const chosen = cfg.broker.callNumber === n.n;
                const res = NUMBER_CASES[numSim];
                return (
                  <div key={n.n}>
                    <button onClick={() => set({ broker: { ...cfg.broker, callNumber: n.n, attestation: res.attestation, cnam: res.attestation ? "registered in 24h" : "pending" } })}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, textAlign: "left", font: "inherit", cursor: "pointer", padding: "11px 14px", borderRadius: 8, background: "#fff", border: `1px solid ${chosen ? T.ink : T.hair}` }}>
                      <span style={{ fontFamily: mono, fontSize: 14 }}>{n.n}</span>
                      <span style={{ fontSize: 12.5, color: T.ink3 }}>{n.area}</span>
                      {chosen && <span style={{ marginLeft: "auto" }}>{res.attestation === "A" ? <Tag solid>attestation A</Tag> : res.attestation === "B" ? <Tag>attestation B</Tag> : <Tag>unavailable</Tag>}</span>}
                    </button>
                    {chosen && numSim !== "a" && <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 8, padding: "10px 12px", border: `1px solid ${T.ink}`, borderRadius: 8 }}>{res.note}</div>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
              <Field half label="Caller ID name" value={cfg.broker.callerId} onChange={(v) => set({ broker: { ...cfg.broker, callerId: v } })} placeholder={cfg.broker.dba} hint="Registered to CNAM. Takes about a day to propagate — until then carriers see the number." />
              <Field half label="Office callback number" value={cfg.broker.officePhone} onChange={(v) => set({ broker: { ...cfg.broker, officePhone: v } })} placeholder="(713) 555-0288" isMono />
              <Field half label="Office time zone"><Select value={cfg.broker.zone} onChange={(v) => set({ broker: { ...cfg.broker, zone: v } })} options={["Eastern", "Central", "Mountain", "Pacific"]} /></Field>
              <Field half label="Who holds your COI and W-9" value={cfg.broker.coi} onChange={(v) => set({ broker: { ...cfg.broker, coi: v } })} placeholder="Name or department" />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={cfg.broker.attestation !== "A"} onClick={onNext}>Next: what Alfred touches</Button>
            <Button kind="quiet" onClick={onBack}>Back</Button>
            {cfg.broker.attestation !== "A" && <span style={{ fontSize: 12.5, color: T.ink3, alignSelf: "center" }}>Pick a number that provisions at attestation A.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- step 3: scope ------------------------------- */

function ScopeStep({ cfg, set, onBack, onNext }) {
  const s = cfg.scope;
  const setS = (patch) => set({ scope: { ...s, ...patch } });
  const toggle = (k, v) => setS({ [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] });
  const wide = s.branches.length === BRANCHES.length && s.mode === "all" && s.goLiveFrom === "everything on the board";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Which loads does Alfred touch?"
        note="Nobody hands an AI the whole board on day one. Alfred ignores everything outside this rule — it won't call, won't post, won't write. The board shows out-of-scope loads greyed, so you can see what it's leaving alone." />

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
        <Micro style={{ marginBottom: 8 }}>Branches</Micro>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{BRANCHES.map((b) => <Pill key={b} on={s.branches.includes(b)} onClick={() => toggle("branches", b)}>{b}</Pill>)}</div>

        <div style={{ marginTop: 18 }}>
          <Micro style={{ marginBottom: 8 }}>Within those branches</Micro>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[["all", "Every load", "The whole board for the branches above."],
              ["equipment", "By equipment", "Start with dry van, where the script is simplest, before reefer or flatbed."],
              ["customer", "By customer", "Pick accounts that tolerate a hiccup and know what you're doing."]].map(([v, t, d]) => (
              <button key={v} onClick={() => setS({ mode: v })} style={{ display: "flex", gap: 12, textAlign: "left", font: "inherit", cursor: "pointer", padding: "12px 14px", borderRadius: 9, background: "#fff", border: `1px solid ${s.mode === v ? T.ink : T.hair}` }}>
                <span style={{ width: 15, height: 15, borderRadius: 999, marginTop: 2, flexShrink: 0, boxSizing: "border-box", border: `${s.mode === v ? 5 : 1}px solid ${s.mode === v ? T.ink : T.hair}`, background: "#fff" }} />
                <span><span style={{ fontSize: 14.5, fontWeight: 500 }}>{t}</span><span style={{ display: "block", fontSize: 12.5, color: T.ink2, marginTop: 3, lineHeight: 1.5 }}>{d}</span></span>
              </button>
            ))}
          </div>
          {s.mode === "equipment" && <div className="alf-fade" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>{EQUIPMENT.map((e) => <Pill key={e} on={s.equipment.includes(e)} onClick={() => toggle("equipment", e)}>{e}</Pill>)}</div>}
          {s.mode === "customer" && <div className="alf-fade" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>{CUSTOMER_LIST.map((c) => <Pill key={c} on={s.customers.includes(c)} onClick={() => toggle("customers", c)}>{c}</Pill>)}</div>}
        </div>

        <div style={{ marginTop: 18 }}>
          <Micro style={{ marginBottom: 8 }}>Always exclude</Micro>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{CUSTOMER_LIST.map((c) => <Pill key={c} on={s.excludeCustomers.includes(c)} onClick={() => toggle("excludeCustomers", c)}>{c}</Pill>)}</div>
          <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>Exclusions win over everything above. Use this for the account that would fire you over one bad call.</div>
        </div>

        <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${T.hairSoft}`, display: "flex", flexDirection: "column", gap: 12 }}>
          <Field half label="Apply to"><Select value={s.goLiveFrom} onChange={(v) => setS({ goLiveFrom: v })} options={["new loads only", "everything on the board"]} /></Field>
          <Check on={s.myLoadsOnly} onChange={(v) => setS({ myLoadsOnly: v })} label="Only loads where the signed-in rep is the load's rep" note="Useful in an agent model, where reps won't accept Alfred touching someone else's freight." />
          <Check on={s.vetAllCarriers} onChange={(v) => setS({ vetAllCarriers: v })} label="Run carrier verification everywhere, even outside the scope above" note="Recommended. Vetting is read-only and costs nothing to widen — it's the calling you want narrow." />
          <Check on={s.inbound} onChange={(v) => setS({ inbound: v })} label="Answer the carrier line after hours from day one" note="The lowest-risk place to start, because the alternative today is voicemail." />
        </div>
      </div>

      {wide && (
        <div style={{ border: `1px solid ${T.ink}`, borderRadius: 10, padding: "13px 16px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, lineHeight: 1.55, flex: 1, minWidth: 260 }}><Tag solid>wide</Tag> <span style={{ marginLeft: 6 }}>Every load, every branch, including freight already in motion. Allowed, but most brokerages start narrower.</span></span>
          <Button size="sm" kind="quiet" onClick={() => setS({ branches: [BRANCHES[0]], mode: "equipment", equipment: ["Dry van"], goLiveFrom: "new loads only" })}>Narrow it for me</Button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 18 }}>
        <Button disabled={s.branches.length === 0 || (s.mode === "equipment" && !s.equipment.length) || (s.mode === "customer" && !s.customers.length)} onClick={onNext}>Next: your team</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

/* ------------------------------ step 4: team ------------------------------- */

const BLANK = { name: "", role: "Carrier sales", branch: "Houston", phone: "", hours: "Mon–Fri, 7:00a–6:00p", afterHours: false, ooo: false, notify: "Phone call", can: ["cover"] };
const ROLES = ["Carrier sales", "Customer/account manager", "Compliance", "Accounting", "After hours", "Branch manager"];

function TeamStep({ cfg, set, onBack, onNext }) {
  const [draft, setDraft] = useState(BLANK);
  const [adding, setAdding] = useState(cfg.team.length === 0);
  const team = cfg.team, ladder = cfg.ladder;
  const ready = draft.name.trim() && draft.phone.trim();
  const missing = PERMS.filter(([k]) => !canApprove(cfg, k)).map(([, l]) => l);
  const reachable = (perm) => ladder.some((r) => {
    if (r.who === "manager") return team.some((t) => t.role === "Branch manager" && t.can.includes(perm) && !t.ooo);
    if (r.who === "rep" || r.who === "second") return team.some((t) => t.can.includes(perm) && !t.ooo);
    const p = team.find((t) => t.id === r.who);
    return p && p.can.includes(perm) && !p.ooo;
  });
  const unreachable = PERMS.filter(([k]) => canApprove(cfg, k) && !reachable(k)).map(([, l]) => l);
  const dupes = ladder.length !== new Set(ladder.map((r) => r.who)).size;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Who does Alfred hand off to?" note="Alfred stops on rate, money, verification, and claims. These are the people it calls, what each may sign off on, and how they'd rather hear about it." />

      {team.length > 0 && (
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
          {team.map((c, i) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, flexWrap: "wrap", opacity: c.ooo ? 0.55 : 1 }}>
              <span style={{ flex: 1, minWidth: 180 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{c.name}</span>
                  <Soft>{c.role}</Soft><Soft>{c.branch}</Soft>
                  {c.afterHours && <Tag>after hours</Tag>}
                  {c.ooo && <Tag solid>out of office</Tag>}
                  {c.can.map((k) => <Soft key={k}>{k}</Soft>)}
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2 }}><span style={{ fontFamily: mono }}>{c.phone}</span> · {c.hours} · notify by {c.notify.toLowerCase()}</span>
              </span>
              <button onClick={() => set({ team: team.map((x) => (x.id === c.id ? { ...x, ooo: !x.ooo } : x)) })} style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>{c.ooo ? "Back in" : "Out of office"}</button>
              <button onClick={() => set({ team: team.filter((x) => x.id !== c.id) })} style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: "pointer" }}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <Field half label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Required" />
            <Field half label="Phone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} placeholder="Required" isMono />
            <Field half label="Role"><Select value={draft.role} onChange={(v) => setDraft({ ...draft, role: v })} options={ROLES} /></Field>
            <Field half label="Branch"><Select value={draft.branch} onChange={(v) => setDraft({ ...draft, branch: v })} options={[...BRANCHES, "All branches"]} /></Field>
            <Field half label="Available hours" value={draft.hours} onChange={(v) => setDraft({ ...draft, hours: v })} />
            <Field half label="Notify a handoff by"><Select value={draft.notify} onChange={(v) => setDraft({ ...draft, notify: v })} options={NOTIFY} /></Field>
          </div>
          <div>
            <Micro style={{ marginBottom: 7 }}>What they can approve</Micro>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {PERMS.map(([k, l]) => <Pill key={k} on={draft.can.includes(k)} onClick={() => setDraft({ ...draft, can: draft.can.includes(k) ? draft.can.filter((x) => x !== k) : [...draft.can, k] })}>{l}</Pill>)}
            </div>
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>Also controls what they see — branch-scoped people don't see another branch's margin.</div>
          </div>
          <Check on={draft.afterHours} onChange={(v) => setDraft({ ...draft, afterHours: v })} label="After-hours fallback" note="Takes handoffs when nobody else is on shift." />
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={!ready} onClick={() => { set({ team: [...team, { ...draft, id: "p" + Math.random().toString(36).slice(2, 7) }] }); setDraft(BLANK); setAdding(false); }}>Add person</Button>
            {team.length > 0 && <Button kind="quiet" onClick={() => { setAdding(false); setDraft(BLANK); }}>Cancel</Button>}
          </div>
        </div>
      ) : <Button kind="quiet" onClick={() => setAdding(true)}>Add another person</Button>}

      {team.length > 0 && (
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Escalation ladder</div>
          <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5 }}>How long a handoff waits before it climbs. Anyone marked out of office is skipped automatically. Verification and claims skip all of it.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
            {ladder.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 18 }}>{i + 1}</span>
                <select value={r.who} onChange={(e) => set({ ladder: ladder.map((x, j) => (j === i ? { ...x, who: e.target.value } : x)) })} style={{ ...inputBase, width: "auto", flex: 1, minWidth: 190, fontSize: 13, padding: "7px 10px", appearance: "none", cursor: "pointer" }}>
                  <option value="rep">The load's rep</option>
                  <option value="second">The load's second contact</option>
                  <option value="manager">The branch manager</option>
                  {team.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.role}</option>)}
                </select>
                <input value={r.at} onChange={(e) => set({ ladder: ladder.map((x, j) => (j === i ? { ...x, at: Number(e.target.value.replace(/\D/g, "") || 0) } : x)) })} style={{ ...inputBase, width: 70, fontFamily: mono, fontSize: 13, padding: "7px 9px" }} />
                <span style={{ fontSize: 12.5, color: T.ink3 }}>minutes</span>
                {ladder.length > 1 && <button onClick={() => set({ ladder: ladder.filter((_, j) => j !== i) })} style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: "pointer" }}>Remove</button>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}><Button size="sm" kind="quiet" onClick={() => set({ ladder: [...ladder, { who: "manager", at: 60 }] })}>Add a rung</Button></div>
          {dupes && <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 12, lineHeight: 1.5 }}>Two rungs point at the same person. That's allowed — it just means the second one does nothing.</div>}
        </div>
      )}

      {team.length > 0 && (missing.length > 0 || unreachable.length > 0) && (
        <Problem title={missing.length ? `Nobody can approve: ${missing.join(", ")}` : `On the roster but not on the ladder: ${unreachable.join(", ")}`}
          body={missing.length
            ? "Handoffs of that type will sit in the queue with nobody able to clear them. Add someone, or give an existing person the permission."
            : "Someone has the permission but no rung reaches them, so Alfred will call people who can't help. Add a rung that points at them."} />
      )}

      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 18 }}>
        <Button disabled={team.length === 0 || missing.length > 0} onClick={onNext}>Next: carrier rules</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

/* -------------------------- step 5: carrier rules -------------------------- */

function VettingStep({ cfg, set, onBack, onNext }) {
  const r = cfg.rules;
  const setR = (patch) => set({ rules: { ...r, ...patch } });
  const [kill, setKill] = useState("");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="What makes a carrier bookable" note="Alfred checks every carrier against these. It never approves one itself — it clears the checks or hands you the exception." />

      <div style={{ border: `1px solid ${T.ink}`, borderRadius: 10, padding: "16px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}><Tag solid>fraud</Tag><span style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Identity checks</span></div>
        <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.55 }}>
          Authority age catches the lazy version. The sharper tells are a record that changed last week, a VOIP number, an address shared across MCs, and a driver at the dock who names a different company than the one you booked.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.hairSoft}` }}>
          <Check on={r.fmcsaCallback} onChange={(v) => setR({ fmcsaCallback: v })} label="Call the FMCSA-registered number before a first load" />
          <Check on={r.recordChange} onChange={(v) => setR({ recordChange: v })} label="Flag contact or address changes on the FMCSA record in the last 30 days" note="The actual hijack signature." />
          <Check on={r.voip} onChange={(v) => setR({ voip: v })} label="Flag VOIP numbers and addresses shared across multiple MCs" />
          <Check on={r.oos} onChange={(v) => setR({ oos: v })} label="Flag out-of-service rates above your threshold" />
          <Check on={r.reactivation} onChange={(v) => setR({ reactivation: v })} label="Flag authority reactivated or transferred in the last 30 days" />
          <Check on={r.doubleBroker} onChange={(v) => setR({ doubleBroker: v })} label="Ask the driver at pickup who dispatched them" note="Catches re-brokering after a booking that looked clean. A mismatch stops the load." />
        </div>
      </div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "16px 18px" }}>
        <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Thresholds and insurance</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
          <Field half label="Minimum authority age" value={r.minAge} onChange={(v) => setR({ minAge: v })} />
          <Field half label="Safety rating"><Select value={r.safety} onChange={(v) => setR({ safety: v })} options={["Satisfactory or unrated", "Satisfactory only", "Anything but Unsatisfactory"]} /></Field>
          <Field half label="Max out-of-service rate" value={r.maxOos} onChange={(v) => setR({ maxOos: v })} isMono />
          <Field half label="Auto liability minimum" value={r.autoLiability} onChange={(v) => setR({ autoLiability: v })} isMono />
          <Field half label="Cargo minimum" value={r.cargo} onChange={(v) => setR({ cargo: v })} isMono />
          <Field half label="Reefer breakdown endorsement" value={r.breakdown} onChange={(v) => setR({ breakdown: v })} isMono hint="Required on temp-controlled loads. A reefer claim without it is the classic uncovered loss." />
          <Field half label="Trailer interchange minimum" value={r.interchange} onChange={(v) => setR({ interchange: v })} isMono />
          <Field half label="Flag insurance expiring within" value={r.insWindow} onChange={(v) => setR({ insWindow: v })} />
          <Field half label="Re-check insurance"><Select value={r.insCadence} onChange={(v) => setR({ insCadence: v })} options={["Daily", "Every 12 hours", "On every booking"]} /></Field>
          <Field half label="If a certificate lapses mid-load"><Select value={r.insMidLoad} onChange={(v) => setR({ insMidLoad: v })} options={["Finish the load, block new bookings", "Alert compliance immediately and hold at the next stop", "Alert only"]} /></Field>
          <Field half label="Vetting service"><Select value={r.service} onChange={(v) => setR({ service: v })} options={["None yet", "RMIS", "Highway", "Carrier411", "MyCarrierPackets"]} /></Field>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.hairSoft}` }}>
          <Check on={r.requirePacket} onChange={(v) => setR({ requirePacket: v })} label="Require a signed packet, W-9, and a certificate naming you as holder" />
          <Check on={r.honorDnu} onChange={(v) => setR({ honorDnu: v })} label="Honor the do-not-use list — carriers you block on the Carriers tab" />
          <Field half label="If a person tries to force a do-not-use carrier"><Select value={r.dnuOverride} onChange={(v) => setR({ dnuOverride: v })} options={["Branch manager only, with a reason", "Compliance only, with a reason", "Nobody — hard block"]} /></Field>
        </div>
      </div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "16px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Alfred may confirm at or below the ceiling</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5 }}>Turns a bid callback from a screening call into a covering call. Alfred still never counters, never names a number first, and never goes a dollar over.</div>
          </div>
          <Toggle on={r.ceiling} onChange={(v) => setR({ ceiling: v })} />
        </div>
        {r.ceiling && (
          <div className="alf-fade" style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.hairSoft}` }}>
            <Micro style={{ marginBottom: 8 }}>Who sets it, by equipment</Micro>
            {EQUIPMENT.map((e) => (
              <div key={e} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", flexWrap: "wrap" }}>
                <span style={{ fontSize: 13.5, minWidth: 90 }}>{e}</span>
                <select value={r.ceilingBy[e]} onChange={(ev) => setR({ ceilingBy: { ...r.ceilingBy, [e]: ev.target.value } })} style={{ ...inputBase, width: "auto", flex: 1, minWidth: 200, fontSize: 13, padding: "6px 10px", appearance: "none", cursor: "pointer" }}>
                  <option>rep sets per load</option><option>never — every rate to a rep</option>
                </select>
              </div>
            ))}
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>Reefer and flatbed are where a bad book hurts most. Plenty of brokerages let Alfred confirm dry van and hold the rest.</div>
          </div>
        )}
      </div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "16px 18px" }}>
        <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Never call these MCs again</div>
        <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5 }}>Brokerage-level kill switch. Above the do-not-use list and above any manager override — Alfred will not dial these numbers for any reason.</div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <input value={kill} onChange={(e) => setKill(e.target.value)} placeholder="MC-000000" style={{ ...inputBase, width: 180, fontFamily: mono, fontSize: 13, padding: "7px 10px" }} />
          <Button size="sm" disabled={!kill.trim()} onClick={() => { setR({ killSwitch: [...r.killSwitch, kill.trim()] }); setKill(""); }}>Add</Button>
        </div>
        {r.killSwitch.length > 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            {r.killSwitch.map((m) => (
              <span key={m} style={{ display: "inline-flex", alignItems: "center", gap: 7, border: `1px solid ${T.ink}`, borderRadius: 6, padding: "4px 9px", fontFamily: mono, fontSize: 12 }}>
                {m}<button onClick={() => setR({ killSwitch: r.killSwitch.filter((x) => x !== m) })} style={{ font: "inherit", background: "none", border: "none", cursor: "pointer", color: T.ink3 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 18 }}>
        <Button onClick={onNext}>Next: test call</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

/* --------------------------- step 6: test + go live ------------------------ */

const TEST_CASES = {
  pass: { ok: true, lines: ["Placing the call", "You answered", "Number verified at attestation A", "Caller ID showed your company name"] },
  spam: { ok: false, title: "Your carrier labeled the call", body: "The call connected, but the handset showed \"Spam Likely.\" That's a carrier-side reputation flag on a freshly provisioned number. It usually clears in a few days of clean traffic — or we can provision a different one now.", lines: ["Placing the call", "You answered", "Handset showed Spam Likely"] },
  noanswer: { ok: false, title: "No answer", body: "The call rang out. Either the number was wrong or your handset screened it. Check the number and try again.", lines: ["Placing the call", "Ringing", "No answer after 30 seconds"] },
  cnam: { ok: false, title: "Caller ID showed the number, not your name", body: "CNAM registration hasn't propagated yet — it takes about a day. The call is fine; carriers will just see a number until it lands.", lines: ["Placing the call", "You answered", "Number verified at attestation A", "Caller ID showed the raw number"] },
};

function TestStep({ cfg, set, onBack, onFinish, done }) {
  const [phase, setPhase] = useState("idle");
  const [line, setLine] = useState(0);
  const [to, setTo] = useState("");
  const [sim, setSim] = useState("pass");
  const c = TEST_CASES[sim];

  useEffect(() => {
    if (phase !== "calling") return;
    if (line < c.lines.length) { const t = setTimeout(() => setLine((n) => n + 1), 650); return () => clearTimeout(t); }
    const t = setTimeout(() => setPhase(c.ok ? "passed" : "failed"), 450);
    return () => clearTimeout(t);
  }, [phase, line]);

  if (done) {
    return (
      <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "flex-start", paddingTop: 8 }}>
        <Waveform active />
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.6 }}>{cfg.broker.dba} is configured</div>
        <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.55, maxWidth: 520 }}>
          {cfg.live ? "Alfred is live and working the board within your scope." : "Alfred is set up but dark — nothing dials until you turn it on from the board."}
        </div>
        <Button onClick={onFinish}>Go to the board</Button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Hear it before you ship it" note="Alfred calls you from your new number. This proves the provisioning, the attestation, the caller ID, and the voice." />

      <Sim value={sim} onChange={(v) => { setSim(v); setPhase("idle"); }} options={[["pass", "Passes"], ["spam", "Spam labeled"], ["noanswer", "No answer"], ["cnam", "CNAM pending"]]} />

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, maxWidth: 540 }}>
        {phase === "idle" && (
          <>
            <Field label="Call me at" value={to} onChange={setTo} placeholder="(713) 555-0100" isMono hint={`Alfred will call from ${cfg.broker.callNumber || "your new number"} and run the carrier verification script.`} />
            <div style={{ marginTop: 14 }}><Button disabled={!to.trim()} onClick={() => { setLine(0); setPhase("calling"); }}>Call me now</Button></div>
          </>
        )}
        {phase === "calling" && <Progress lines={c.lines} at={line} />}
        {phase === "failed" && <Problem title={c.title} body={c.body}><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button size="sm" onClick={() => { setLine(0); setPhase("calling"); }}>Re-test</Button><Button size="sm" kind="quiet" onClick={() => setPhase("idle")}>Change the number</Button></div></Problem>}
        {phase === "passed" && (
          <div className="alf-fade">
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}><Tag solid>passed</Tag><span style={{ fontSize: 15, fontWeight: 600 }}>Everything checked out</span></div>
            <div style={{ marginTop: 12 }}>
              <Row label="Called from" value={cfg.broker.callNumber} isMono />
              <Row label="Attestation" value={`${cfg.broker.attestation} — full`} />
              <Row label="Caller ID" value={cfg.broker.callerId || cfg.broker.dba} />
              <Row label="Disclosure" value="Played at second one" />
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}`, lineHeight: 1.55 }}>
              <Micro style={{ marginBottom: 5 }}>What this does not prove</Micro>
              It doesn't test the call plan, the TMS write-back, your carriers' willingness to answer an unknown number, or anything about how Alfred handles a real conversation. It proves the line works and it sounds right.
            </div>
            <div style={{ marginTop: 12 }}><Button size="sm" kind="quiet" onClick={() => { setLine(0); setPhase("calling"); }}>Run it again</Button></div>
          </div>
        )}
      </div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "16px 18px" }}>
        <Micro style={{ marginBottom: 8 }}>The moment you finish</Micro>
        {[["Alfred answers your carrier line after hours", cfg.scope.inbound && cfg.live, cfg.scope.inbound ? (cfg.live ? "Live from tonight" : "Waiting on the switch below") : "Off — carriers get voicemail as they do today"],
          ["Alfred works loads in scope", cfg.live && TMS_MODES[cfg.tms.mode].canCall, TMS_MODES[cfg.tms.mode].canCall ? (cfg.live ? `${cfg.scope.branches.join(", ")}${cfg.scope.mode === "equipment" ? ` · ${cfg.scope.equipment.join(", ")}` : ""}` : "Waiting on the switch below") : "Watch mode — nothing dials"],
          ["Alfred writes back to your TMS", cfg.live && TMS_MODES[cfg.tms.mode].canWrite, TMS_MODES[cfg.tms.mode].canWrite ? (cfg.live ? "On the order, prefixed ALFRED:" : "Waiting on the switch below") : "No — read only"],
          ["Everything outside scope", false, "Untouched. Your reps work it exactly as they do now."]].map(([label, on, detail]) => (
          <div key={label} style={{ display: "flex", gap: 12, padding: "9px 0", borderTop: `1px solid ${T.hairSoft}`, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13.5, flex: 1, minWidth: 220 }}>{label}</span>
            <span style={{ fontSize: 12.5, color: T.ink2, textAlign: "right" }}>{detail}</span>
            {on ? <Tag solid>on</Tag> : <Soft>off</Soft>}
          </div>
        ))}
      </div>

      <div style={{ border: `1px solid ${cfg.live ? T.ink : T.hair}`, borderRadius: 10, padding: "16px 18px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Turn Alfred on</div>
          <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5 }}>
            Until this is on, Alfred is dark — it reads, it drafts, it dials nothing. You can flip it either way from the board at any time.
          </div>
        </div>
        <Toggle on={cfg.live} onChange={(v) => set({ live: v })} />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button disabled={phase !== "passed"} onClick={onFinish}>Finish setup</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
        {phase !== "passed" && <span style={{ fontSize: 12.5, color: T.ink3, alignSelf: "center" }}>Run a passing test call first.</span>}
      </div>
    </div>
  );
}

/* ----------------------------------- app ----------------------------------- */

/* ─────────────────────────────────────────────────────────────────────────────
   PROTOTYPE BANNER — AND THIS SCREEN DOES SAVE SOMETHING.
   The TMS connection, the counts it reports finding, the carrier and customer
   lists and the timers are all seed data defined in this file. But `finish()`
   POSTs /api/broker and CREATES THE BROKERAGE ROW — the row the live Carriers
   tab needs to exist at all ("your brokerage setup is not finished yet").
   ⛔ So the old text, "not connected to live calls, and not saved", was false in
   the dangerous direction on the one screen in this vertical that writes. An
   operator screen that looks live and isn't is the failure this platform keeps
   paying for; one that IS writing and says it isn't is the same failure pointed
   the other way.
   ───────────────────────────────────────────────────────────────────────────── */
function PrototypeBanner({ what }) {
  return (
    <div style={{
      background: "#000", color: "#fff", fontFamily: sans, fontSize: 12.5,
      padding: "7px 22px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
    }}>
      <span style={{
        fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase",
        border: "1px solid #fff", borderRadius: 3, padding: "1px 5px"
      }}>prototype</span>
      <span>{what} The figures and lists here are sample data and no call is placed — but finishing setup does create your brokerage, which is what the live carrier file needs.</span>
      <Link to="/broker" style={{ marginLeft: "auto", color: "#fff", fontSize: 12.5 }}>← Brokerage screens</Link>
    </div>
  );
}

export default function AlfredBrokerOnboarding({ onDone }) {
  // ⛔ A RE-RUN STARTS FROM WHAT IS ACTUALLY SAVED, not from the placeholders.
  //
  // This read `getDraft()` alone, and `hydrate()` clears the draft — so an operator who
  // reopened Brokerage setup after a refresh saw DEFAULT_CONFIG's Gulf Bridge placeholders,
  // and finishing re-saved those over their real team, scope and vetting rules. Silently.
  const resumed = getDraft();
  const existing = getConfig();
  const [cfg, setCfg] = useState(resumed || existing || DEFAULT_CONFIG);
  const [step, setStep] = useState(resumed || existing ? 1 : 0);
  const [done, setDone] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const LIST = ["TMS", "Brokerage", "Scope", "Team", "Carrier rules", "Go live"];

  const [saveError, setSaveError] = useState(null);

  /**
   * ⛔ THIS USED TO FINISH WITHOUT SAVING ANYTHING.
   *
   * `commitConfig` writes to a module-level variable in shared/brokerConfig.js — its own
   * comment says "In production this is a row per brokerage". So a broker could complete
   * every step, watch the success screen, refresh, and be back at step one with no record
   * they had ever signed up. The screen was not lying on purpose; nobody had built the
   * other end. Now there is one.
   *
   * ⛔ AND IT NO LONGER CLAIMS SUCCESS BEFORE THE SAVE LANDS. `setDone(true)` ran first and
   * unconditionally, so a failure still showed "you're live". The in-memory commit stays,
   * because the Board reads it within this session, but it happens AFTER the server
   * confirms — and a failure says so and leaves the operator on the step with their answers
   * intact, rather than sending somebody who already completed a sign-up back to step one.
   */
  async function finish() {
    setSaveError(null);
    try {
      const body = {
        name: cfg.broker?.legalName || cfg.broker?.dba || 'My Brokerage',
        mcNumber: cfg.broker?.mc || undefined,
        dotNumber: cfg.broker?.usdot || undefined,
        phone: cfg.broker?.officePhone || undefined,
        // What the authority lookup established — stored verbatim, never interpreted. It
        // cannot confirm this is a BROKER rather than a carrier; see broker.mjs.
        authority: cfg.broker?.verified
          ? {
            found: true,
            authorized: cfg.broker?.status === 'Active',
            status: cfg.broker?.status || null,
            carrierName: cfg.broker?.legalName || null,
            mock: !!cfg.broker?.mock,
          }
          : undefined,
      };
      const saved = await freight.saveBrokerage(body);
      const final = commitConfig({ ...cfg, brokerageId: saved?.id ?? null });
      setDone(true);
      if (onDone) onDone(final);
    } catch (err) {
      setSaveError(err?.body?.error === 'name_required'
        ? 'The brokerage needs a legal name before it can be saved.'
        : `Could not save: ${err?.body?.message || err?.message || 'unknown error'}. Nothing was lost — try again.`);
    }
  }

  return (
    <div className="alf-root" style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        .alf-bar { animation: alfBar 1s ease-in-out infinite; transform-origin: bottom; }
        @keyframes alfBar { 0%,100% { transform: scaleY(.28);} 50% { transform: scaleY(1);} }
        .alf-fade { animation: alfFade .26s cubic-bezier(.4,0,.2,1); }
        @keyframes alfFade { from { opacity:0; transform: translateY(4px);} to { opacity:1; transform:none;} }
        .alf-row:hover { background:#FAFAFA; }
        .alf-btn { transition: opacity .15s, transform .1s; }
        .alf-btn:active { transform: scale(.975); }
        .alf-root input::placeholder { color:${T.ink4}; }
        .alf-root input:focus, .alf-root select:focus { border-color:#000 !important; }
        .alf-root button:focus-visible, .alf-root input:focus-visible, .alf-root select:focus-visible { outline:2px solid #000; outline-offset:2px; }
        @media (prefers-reduced-motion: reduce) { .alf-bar,.alf-fade { animation:none !important; } }
      `}</style>

      <PrototypeBanner what="Brokerage setup." />


      <header style={{ borderBottom: `1px solid ${T.hair}`, background: "#fff" }}>
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 22px", height: 54, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4 }}>Alfred</span>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>Brokerage setup</span>
          {!done && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
              {saved && <span style={{ fontSize: 12, color: T.ink3 }}>Draft saved</span>}
              <Button size="sm" kind="quiet" onClick={() => { saveDraft(cfg); setSaved(true); setTimeout(() => setSaved(false), 2500); }}>Save and finish later</Button>
            </div>
          )}
        </div>
      </header>

      <main style={{ maxWidth: 880, margin: "0 auto", padding: "26px 22px 70px" }}>
        <div style={{ marginBottom: 24 }}><Steps list={LIST} current={done ? 6 : step} /></div>
        {/* ⛔ A FAILED SAVE HAS TO BE ON SCREEN. Before this, finish() set done=true
            unconditionally and persisted nothing at all, so every outcome looked like
            success. An error that is caught and never rendered is the same bug with extra
            steps — the operator still walks away believing they signed up. */}
        {saveError && (
          <div role="alert" style={{
            marginBottom: 16, border: `1px solid ${T.hair}`, borderLeft: `3px solid ${T.ink}`,
            borderRadius: 8, background: '#fff', padding: '12px 14px', fontSize: 13.5, lineHeight: 1.5,
          }}>{saveError}</div>
        )}
        <div style={{ background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, padding: "26px 26px 28px" }}>
          {step === 0 && <TmsStep cfg={cfg} set={set} onNext={() => setStep(1)} />}
          {step === 1 && <BrokerageStep cfg={cfg} set={set} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
          {step === 2 && <ScopeStep cfg={cfg} set={set} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
          {step === 3 && <TeamStep cfg={cfg} set={set} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
          {step === 4 && <VettingStep cfg={cfg} set={set} onBack={() => setStep(3)} onNext={() => setStep(5)} />}
          {step === 5 && <TestStep cfg={cfg} set={set} done={done} onBack={() => setStep(4)} onFinish={finish} />}
        </div>
      </main>
    </div>
  );
}
